import { Theme } from '@mui/material/styles'

// ----------------------------------------------------------------------
// A card is flat. Elevation means "this floats above the page", and a card
// does not, so its edge is a hairline border and its resting shadow is e0.
// Twenty resting shadows on one list page read as corrugation.
//
// Padding is s5 (20) all round. The header sits on 16 / 20 with nothing below
// it, so the content's own top padding is the only gap between title and body
// and the two never stack to 40. The title is the `cardTitle` role, which the
// type scale carries as `h4`.
// ----------------------------------------------------------------------

/**
 * The width of a card's hairline edge. Exported for a card whose content
 * states the width rather than the card (the map callout, which sizes to its
 * content so a scrollbar is added beside the content instead of taken out of
 * it), so the two together still come to the card's own width.
 */
export const CARD_EDGE_WIDTH = 1

export default function Card(theme: Theme) {
  return {
    MuiCard: {
      styleOverrides: {
        root: {
          position: 'relative',
          boxShadow: theme.customShadows.card, // e0, which is 'none'
          backgroundImage: 'none',
          border: `${CARD_EDGE_WIDTH}px solid ${theme.palette.divider}`,
          borderRadius: theme.radii.surface,
          zIndex: 0, // Fix Safari overflow: hidden with border radius
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: { variant: 'h4' },
        subheaderTypographyProps: {
          variant: 'body2',
          marginTop: theme.spacing(0.5),
        },
      },
      styleOverrides: {
        root: {
          // The head wraps. MUI lays it out as a nowrap flex row and gives the
          // action `flex: 0 0 auto`, so a head whose actions do not fit does
          // not fold: it runs the action out of the card, and the card Paper
          // clips it. Measured on the vehicle rentals card at 390: a 356px
          // header with a 308px action block, laid out at 109..417, which is
          // 68px past the card. `Export these results` was not scrolled out of
          // reach, it was gone.
          //
          // The row gap only exists once it has folded, so this costs a head
          // that fits nothing at all.
          padding: theme.spacing(2, 2.5, 0),
          flexWrap: 'wrap',
          rowGap: theme.spacing(1),
        },
        // MUI pulls the action block up and out by a few pixels to line an
        // icon button's glyph up with the title. Our controls are square boxes
        // on a fill, so they centre on the title row instead.
        //
        // `marginLeft: auto` is what keeps it on the right once it has wrapped:
        // on its own line it is the only item, and the title's `flex: 1 1 auto`
        // is no longer there to push it over.
        //
        // Folding gives the action a line of its own, and no more than that
        // line: `flex: 0 0 auto` sizes it to all of its buttons side by side,
        // so a block wider than the whole head still ran past the card on its
        // own line. Measured at 390 on the vehicle page: Vehicle Assessments'
        // `Add New Vehicle Assessment Images` 73px past the card, cut at
        // `Assessmen`, and Vehicle Rentals' second export 5px past. Held to
        // the head's width, the row inside (an `ActionRow`, which wraps)
        // folds its buttons onto lines of their own instead.
        action: {
          margin: 0,
          marginLeft: 'auto',
          alignSelf: 'center',
          maxWidth: '100%',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          'padding': theme.spacing(2.5),
          // MUI's own rule for the last child is more specific than `root`,
          // so it has to be restated or the bottom stays at MUI's 24.
          '&:last-child': {
            paddingBottom: theme.spacing(2.5),
          },
        },
      },
    },
  }
}
